import { theme } from '@branchjs/neo-components';
import * as R from 'ramda';
import { COMPLETE, INCOMPLETE } from '../../../common/components/core/StepperCard/stepConstants';
import DiscoAdsCreateAdGroup from './DiscoAdsCreator/DiscoAdsCreateAdGroup';
import DiscoAdsCreateCampaign from './DiscoAdsCreator/DiscoAdsCreateCampaign';
import DiscoAdsCreateCreative from './DiscoAdsCreator/DiscoAdsCreateCreative';

// NOTE: DA is an acronym for Discovery Ads

// ------------------------------------------------------------------------- //
// ----------------------- NAMESPACES AND URL PATHS------------------------- //
// ------------------------------------------------------------------------- //

export const DISCO_ADS_ROUTE = 'mobile-search';

export const DA_CAMPAIGNS_VIEW_NAMESPACE = 'campaigns';

export const DA_AD_GROUPS_VIEW_NAMESPACE = 'ad_groups';

export const DA_CREATIVES_VIEW_NAMESPACE = 'creatives';

export const DA_NAMESPACE = 'disco_ads';

export const DA_CREATOR_NAMESPACE = 'creator';

export const DA_CAMPAIGN_CREATOR_NAMESPACE = 'campaignFlow';

export const DA_AD_GROUP_CREATOR_NAMESPACE = 'adGroupFlow';

export const DA_CREATOR_PATH = '/mobile-search/promotions/create';

export const DA_MANAGER_PATH = '/mobile-search/promotions/manage';

export const DA_CURRENT_CAMPAIGN = 'campaign';

export const DA_CURRENT_AD_GROUP = 'adGroup';

export const ALL = 'all';

export const ACTIVE = 'active';

export const PAUSED = 'paused';

// ------------------------------------------------------------------------- //
// -------------------------- TEXT AND BUTTON COPY-------------------------- //
// ------------------------------------------------------------------------- //

export const DA_CAMPAIGN_VIEW_TITLE = 'Manage Your Campaigns';

export const DA_CAMPAIGN_VIEW_SUBTITLE = 'Create and manage your promoted results';

export const DA_AD_GROUPS_VIEW_TITLE = 'Manage Your Promoted Result Groups';

export const DA_CREATIVES_VIEW_TITLE = 'Manage Your Creative Sets';

export const DA_CREATE_CAMPAIGN_BTN_COPY = 'Create Campaign';

export const DA_CREATE_AD_GROUP_BTN_COPY = 'Create Ad Group';

export const DA_ADD_CREATIVE_BTN_COPY = 'Add Creative Set';

export const DA_SETTINGS_MODAL_COPY = {
  campaign: 'Edit Campaign Settings',
  adGroup: 'Edit Ad Group Settings',
  addCreative: 'Add Creative Set',
};

export const DA_EDIT_BUTTON_TEXT = {
  campaign: 'Edit Campaign Settings',
  adGroup: 'Edit Ad Group Settings',
};

export const DA_CREATOR_BUTTON_COPY = {
  SAVE: 'Save',
  SAVE_AND_CONTINUE: 'Save & Continue',
  LAUNCH_CAMPAIGN: 'Launch Campaign',
  LAUNCH_AD_GROUP: 'Launch Ad Group',
};

export const AD_OBJECT_SETTINGS_SAVE_BTN_COPY = 'Save';

export const NEW_AD_OBJECT_LABEL_COPY = 'NEW';

// ------------------------------------------------------------------------- //
// -------------------- Table Titles and Filter Options--------------------- //
// ------------------------------------------------------------------------- //

export const DA_CAMPAIGN_TABLE_TITLE = 'Campaigns';

export const DA_AD_GROUPS_TABLE_TITLE = 'Ad Groups';

export const DA_CREATIVES_TABLE_TITLE = 'Creative Sets';

export const DA_FILTER_OPTIONS = [
  {
    label: 'All',
    value: ALL,
  },
  {
    label: 'Active',
    value: ACTIVE,
  },
  {
    label: 'Paused',
    value: PAUSED,
  },
];

export const DA_DEFAULT_DATE_RANGE = 7;

// ------------------------------------------------------------------------- //
// ------------------ DiscoAdsTableStatusWrapper Constants------------------ //
// ------------------------------------------------------------------------- //

export const DA_PAUSED_STATUS = {
  campaign: {
    value: 'Campaign is Paused',
    label: 'Campaign is Paused',
    description:
      'Ad groups in paused campaigns become paused by default. You must activate your campaign to change the status of this ad group.',
  },
  adGroup: {
    value: 'Ad Group is Paused',
    label: 'Ad Group is Paused',
    description:
      'Creative in paused ad groups become paused by default. You must activate your ad group to change the status of your creative.',
  },
};

export const DA_STATUS_COLORS = {
  active: theme.colors.success.main,
  paused: theme.colors.error.main,
  disabled: theme.colors.warning.main,
  selectedOptionBackgroundColor: theme.colors.gray[100],
  optionTextColor: theme.colors.primary.contrastBlack,
};

export const DA_STATUS_OPTIONS = [
  {
    value: ACTIVE,
    label: 'Active',
    dotColor: theme.colors.success.main,
    backgroundColor: theme.colors.success.light,
  },
  {
    value: PAUSED,
    label: 'Paused',
    dotColor: theme.colors.error.main,
    backgroundColor: theme.colors.error.light,
  },
];

// ------------------------------------------------------------------------- //
// -------------------------- Numerical Constants--------------------------- //
// ------------------------------------------------------------------------- //

export const DA_BREADCRUMB_CHAR_LIMIT = 20;

export const DA_CONTENT_URL_TOOLTIP_CHAR_LIMIT = 200;

export const DA_CREATIVE_CHAR_LIMIT = {
  headline: 30,
  description: 40,
};

export const DA_CREATIVE_IMAGE_DIMENSIONS = {
  minWidth: 200,
  minheight: 200,
};

export const DA_CREATIVE_MAX_FILE_SIZE = 1048576; // reported in bytes to match the browser. This is 1MB

// ------------------------------------------------------------------------- //
// ----------------------------- Column Popover Copy------------------------ //
// ------------------------------------------------------------------------- //

const POPOVER_COPY = {
  campaigns: {
    type: 'Campaign Types help Branch optimize your budget depending on your campaign goals (e.g. drive installs, re-engagement).',
    status: 'Status indicates whether a campaign is currently running or not.',
    spend: "Spend denotes the amount of a campaign's budget that has already been spent.",
    budget_remaining: 'Budget Remaining indicates the budget that has not yet been spent.',
    impressions: 'Impressions indicate the unique number of times a user was delivered an ad.',
    clicks: 'Clicks indicate the unique number of times that a user clicked on an ad.',
    average_cpc: 'Average CPC denotes the average cost of a user clicking on an ad.',
    ctr: 'CTR is the fraction of users who clicked on an ad, divided by the number of users who were delivered an ad.',
    installs: 'Installs denotes the number of app installs that occurred as a result of a user clicking on an ad.',
    cti: 'CTI is the fraction of users who installed your app, divided by the number of users who clicked on the ad.',
  },
};

// ------------------------------------------------------------------------- //
// ----------------------------- Table Columns------------------------------ //
// ------------------------------------------------------------------------- //

export const CAMPAIGN_TABLE_COLUMNS = [
  {
    key: 'name',
    display: 'Campaign Name',
    // @ts-ignore
    comparator: R.compose(R.toLower, R.prop('name')),
    popover: { title: 'Campaign Name' },
    width: '250px',
  },
  {
    key: 'type',
    display: 'Type',
    popover: { title: POPOVER_COPY.campaigns.type },
  },
  {
    key: 'status',
    display: 'Status',
    popover: { title: POPOVER_COPY.campaigns.status },
    width: '180px',
  },
  {
    key: 'spend',
    display: 'Spend',
    popover: { title: POPOVER_COPY.campaigns.spend },
  },
  {
    key: 'budget_remaining',
    display: 'Budget Remaining',
    popover: { title: POPOVER_COPY.campaigns.budget_remaining },
  },
  {
    key: 'impressions',
    display: 'Impressions',
    popover: { title: POPOVER_COPY.campaigns.impressions },
  },
  {
    key: 'clicks',
    display: 'Clicks',
    popover: { title: POPOVER_COPY.campaigns.clicks },
  },
  {
    key: 'average_cpc',
    display: 'Average CPC',
    popover: { title: POPOVER_COPY.campaigns.average_cpc },
  },
  {
    key: 'ctr',
    display: 'CTR',
    popover: { title: POPOVER_COPY.campaigns.ctr },
  },
  {
    key: 'installs',
    display: 'Installs',
    popover: { title: POPOVER_COPY.campaigns.installs },
  },
  { key: 'cti', display: 'CTI', popover: { title: POPOVER_COPY.campaigns.cti } },
];

export const AD_GROUP_TABLE_COLUMNS = [
  {
    key: 'name',
    display: 'Ad Group Name',
    // @ts-ignore
    comparator: R.compose(R.toLower, R.prop('name')),
    width: '250px',
  },
  { key: 'status', display: 'Status', width: '180px' },
  { key: 'default_max_cpc', display: 'Default Max CPC', width: '180px' },
  { key: 'spend', display: 'Spend' },
  { key: 'average_cpc', display: 'Average CPC' },
  { key: 'impressions', display: 'Impressions' },
  { key: 'clicks', display: 'Clicks' },
  { key: 'installs', display: 'Installs' },
  { key: 'ctr', display: 'CTR' },
  { key: 'cti', display: 'CTI' },
];

export const CREATIVE_TABLE_COLUMNS = [
  { key: 'icon_url', display: 'Icon', width: '80px' },
  {
    key: 'name',
    display: 'Title',
    // @ts-ignore
    comparator: R.compose(R.toLower, R.prop('name')),
    width: '250px',
  },
  { key: 'description', display: 'Description', width: '250px' },
  { key: 'status', display: 'Status', width: '180px' },
  { key: 'content_url', display: 'Content URL', width: '230px' },
  { key: 'spend', display: 'Spend' },
  { key: 'average_cpc', display: 'Average CPC' },
  { key: 'impressions', display: 'Impressions' },
  { key: 'clicks', display: 'Clicks' },
  { key: 'ctr', display: 'CTR' },
  { key: 'installs', display: 'Installs' },
  { key: 'cti', display: 'CTI' },
];

// ------------------------------------------------------------------------- //
// --------------------------CREATOR CONSTANTS ----------------------------- //
// ------------------------------------------------------------------------- //

export const DA_CAMPAIGN_SETUP = 'campaignSetup';
export const DA_AD_GROUP_SETUP = 'adGroupSetup';
export const DA_CREATIVE_SETUP = 'creativeSetup';

export const DA_CREATOR_TITLE_COPY = {
  [DA_CAMPAIGN_SETUP]: {
    title: 'Create New Campaign',
    subtitle: 'Specify your budget and targeting',
  },
  [DA_AD_GROUP_SETUP]: {
    title: 'Create Ad Group',
    subtitle: 'Choose an ad group name and specify your CPC threshold',
  },
  [DA_CREATIVE_SETUP]: {
    title: 'Add a Creative Set',
    subtitle: 'Specify your ad copy and upload a thumbnail image',
  },
};

export const DA_STEPPER_CARD_STEPS = [
  {
    stepKey: DA_CAMPAIGN_SETUP,
    title: 'Campaign Setup',
    viewComponent: DiscoAdsCreateCampaign,
    testid: 'da-creator-campaign-setup',
  },
  {
    stepKey: DA_AD_GROUP_SETUP,
    title: 'Ad Group Setup',
    viewComponent: DiscoAdsCreateAdGroup,
    testid: 'da-creator-ad-group-setup',
  },
  {
    stepKey: DA_CREATIVE_SETUP,
    title: 'Creative Setup',
    viewComponent: DiscoAdsCreateCreative,
    testid: 'da-creator-creative-setup',
  },
];

export const STEPPER_CARD_COMPLETE_STEP = COMPLETE;
export const STEPPER_CARD_INCOMPLETE_STEP = INCOMPLETE;

export const DA_MOCK_COUNTRY_OPTIONS = [{ label: 'India', value: 'IN' }];

export const DEFAULT_COUNTRY_TARGETING = ['IN'];

export const INSTALL_CAMPAIGN = 'install';
export const RE_ENGAGEMENT_CAMPAIGN = 're_engagement';

export const DA_CAMPAIGN_OBJECTIVE_OPTIONS = [
  { label: 'Install', value: INSTALL_CAMPAIGN },
  { label: 'Re-engagement', value: RE_ENGAGEMENT_CAMPAIGN },
];

export const DA_CREATOR_FORM_SECTION_COPY = {
  campaign: {
    selectedApp: {
      title: 'Selected app',
      subtitle: null,
    },
    geoTargets: {
      title: 'Select your target locations',
      subtitle: 'Please add at least one target location to your campaign',
      searchPlaceholder: 'Search and add locations',
    },
    name: {
      title: 'Campaign Name',
      subtitle: 'Select the name of this campaign',
    },
    budget: {
      title: 'Total Budget',
      subtitle: 'How much money do you want to allocate to this campaign?',
    },
    objective: {
      title: 'Campaign Objective',
      subtitle: null,
    },
  },
  adGroup: {
    name: {
      title: 'Ad Group Name',
      subtitle: 'Ad groups contain settings that determine who may see your ad.',
    },
    defaultBid: {
      title: 'Default Max CPC Bid',
      subtitle: 'Specify your default bid for this ad group',
    },
  },
  creative: {
    headline: {
      title: 'Ad Headline',
      subtitle: 'Choose a name that describes the theme of your Creative Set',
      placeholder: 'Ad Headline',
    },
    description: {
      title: 'Ad Description',
      subtitle: 'Add a brief description which will appear underneath your Ad Headline',
      placeholder: 'Ad Description',
    },
    contentUrl: {
      title: 'Content URL',
      subtitle: 'Add a Branch Deeplink',
      placeholder: 'Enter Branch Deeplink',
    },
    thumbnailImage: {
      title: 'Add a thumbnail image',
      subtitle: 'This image will appear alongside your ad. .Please provide a valid image file ending in .png or .jpeg',
    },
  },
};

export const DA_CREATOR_TOOLTIP_COPY = {
  campaign: {
    selectedApp:
      "You can only create campaigns for the app you're currently viewing. If you have access to more than one app, you may switch apps via the dropdown menu in the upper-left-hand corner of the Branch dashboard.",
    budget: "You can always update your total budget after you've launched your campaign.",
    objective:
      'Specifying a campaign objective will help Branch optimize your ad-spend for the KPIs that are most important to you.',
  },
};

export const DA_CREATIVE_VALID_FILE_EXTENSIONS = ['png', 'jpeg', 'jpg'];

export const GENERIC_ANDROID_DEVICE = {
  width: 288,
  height: 534,
  backgroundImage: 'url(/static/images/discovery_ads/devices/Android_generic.svg)',
};

export const PREVIEWER_DEVICE_OPTIONS = [
  {
    value: 'android_generic',
    label: 'Android',
  },
];
